import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { DashboardLayout } from "../layouts/DashboardLayout";
import { CourseTable, Course } from "../components/courses/CourseTable";
import { DeleteCourseDialog } from "../components/courses/DeleteCourseDialog";
import { ReorderCoursesDialog } from "../components/courses/ReorderCoursesDialog";
import { ExportCoursesDialog } from "../components/courses/ExportCoursesDialog";
import { courseService } from "../services/courseService";
import { useToast } from "../components/ui/ToastContainer";
import { PermissionDenied } from "../components/ui/PermissionDenied";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/Button";
import { RefreshCw, Download } from "lucide-react";

export default function CoursesPage() {
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  const { hasPermission } = useAuth();

  if (!hasPermission('courses:read')) {
    return <PermissionDenied requiredPermission="courses:read" />;
  }
  const [courses, setCourses] = useState<Course[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Server-side State
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [sortField, setSortField] = useState("orderIndex");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  const [statusFilter, setStatusFilter] = useState("all");

  // Dialog States
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isReorderModalOpen, setIsReorderModalOpen] = useState(false);
  const [isResyncConfirmOpen, setIsResyncConfirmOpen] = useState(false);
  const [isResyncing, setIsResyncing] = useState(false);
  const [isExportOpen, setIsExportOpen] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState<Course | null>(null);

  // Debounce Search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setCurrentPage(1); // Reset to page 1 on search change
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const fetchCourses = useCallback(async () => {
    try {
      setIsLoading(true);

      const params: any = {
        page: currentPage,
        limit: pageSize,
        sortBy: sortField,
        sortOrder: sortOrder,
        search: debouncedSearch,
      };

      if (statusFilter === "published") params.isPublished = "true";
      if (statusFilter === "draft") params.isPublished = "false";
      if (statusFilter === "all") params.isPublished = "all";
      if (statusFilter === "archived") params.isPublished = "false";

      const response = await courseService.getAllCourses(params);

      // Handle response structure from backend
      const fetchedCourses = response.data?.courses || [];
      const total =
        response.count || (response.data?.pagination as any)?.totalCourses || 0;

      setCourses(fetchedCourses);
      setTotalItems(total);
    } catch (error) {
      console.error("Failed to fetch courses:", error);
      showError("Failed to load courses");
    } finally {
      setIsLoading(false);
    }
  }, [
    currentPage,
    pageSize,
    sortField,
    sortOrder,
    debouncedSearch,
    statusFilter,
    showError,
  ]);

  useEffect(() => {
    fetchCourses();
  }, [fetchCourses]);

  const handleTogglePublish = async (course: Course) => {
    try {
      await courseService.togglePublishCourse(course._id);
      showSuccess(
        `Course ${
          course.isPublished ? "unpublished" : "published"
        } successfully`
      );
      fetchCourses();
    } catch (error) {
      console.error("Failed to toggle course status:", error);
      showError("Failed to update course status");
    }
  };

  const handleEditClick = (course: Course) => {
    navigate(`/courses/edit/${course._id}`);
  };

  const handleDeleteClick = (course: Course) => {
    setSelectedCourse(course);
    setIsDeleteModalOpen(true);
  };

  const handleViewClick = (course: Course) => {
    navigate(`/courses/edit/${course._id}`);
  };

  const handleManageStructure = (course: Course) => {
    navigate(`/courses/${course._id}/structure`);
  };

  const handleResyncStats = async () => {
    setIsResyncing(true);
    try {
      const response = await courseService.resyncAllCourseStats();
      const successCount = response.data?.data?.filter((r: any) => r.status === 'success').length || 0;
      showSuccess(`Resynced ${successCount} courses successfully`);
      fetchCourses();
    } catch (error) {
      console.error("Failed to resync course stats:", error);
      showError("Failed to resync course stats");
    } finally {
      setIsResyncing(false);
      setIsResyncConfirmOpen(false);
    }
  };

  return (
    <DashboardLayout>
      <CourseTable
        courses={courses}
        description="Create, manage, and organize your course catalog."
        isLoading={isLoading}
        totalItems={totalItems}
        currentPage={currentPage}
        pageSize={pageSize}
        searchTerm={searchTerm}
        statusFilter={statusFilter}
        sortField={sortField}
        sortOrder={sortOrder}
        onSearchChange={setSearchTerm}
        onStatusChange={(status) => {
          setStatusFilter(status);
          setCurrentPage(1);
        }}
        onSortChange={(field, order) => {
          setSortField(field);
          setSortOrder(order);
          setCurrentPage(1);
        }}
        onPageChange={setCurrentPage}
        onAddClick={() => navigate("/courses/add")}
        onEditClick={handleEditClick}
        onDeleteClick={handleDeleteClick}
        onViewClick={handleViewClick}
        onManageStructure={handleManageStructure}
        onTogglePublish={handleTogglePublish}
        onReorderClick={() => setIsReorderModalOpen(true)}
        onResyncStats={() => setIsResyncConfirmOpen(true)}
        extraHeaderActions={
          hasPermission("analytics:export") ? (
            <Button
              variant="outline"
              size="lg"
              className="whitespace-nowrap gap-2 w-full sm:w-auto h-[48px] sm:h-[44px] justify-center"
              onClick={() => setIsExportOpen(true)}
            >
              <Download size={16} />
              Export CSV
            </Button>
          ) : undefined
        }
      />

      <DeleteCourseDialog
        isOpen={isDeleteModalOpen}
        course={selectedCourse}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setSelectedCourse(null);
        }}
        onSuccess={fetchCourses}
      />

      <ExportCoursesDialog
        isOpen={isExportOpen}
        onClose={() => setIsExportOpen(false)}
        initialSearchTerm={searchTerm}
        initialStatus={
          statusFilter === "published" || statusFilter === "draft"
            ? (statusFilter as "published" | "draft")
            : "all"
        }
      />

      <ReorderCoursesDialog
        isOpen={isReorderModalOpen}
        onClose={() => setIsReorderModalOpen(false)}
        onSuccess={fetchCourses}
      />

      {/* Resync Confirmation Dialog */}
      {isResyncConfirmOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full mx-4 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="h-10 w-10 rounded-full bg-amber-100 flex items-center justify-center">
                <RefreshCw size={20} className="text-amber-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Resync All Course Stats</h3>
            </div>
            <p className="text-sm text-gray-600 mb-6">
              This will recalculate <strong>totalLessons</strong>, <strong>totalDuration</strong>, and <strong>duration</strong> for all courses based on their published lessons. This may take a moment depending on the number of courses.
            </p>
            <div className="flex items-center justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => setIsResyncConfirmOpen(false)}
                disabled={isResyncing}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleResyncStats}
                disabled={isResyncing}
              >
                {isResyncing ? (
                  <>
                    <RefreshCw size={16} className="animate-spin mr-2" />
                    Resyncing...
                  </>
                ) : (
                  "Resync Now"
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
